import React, {useState} from 'react';
import { ChevronUp, ChevronDown, Check, List } from 'lucide-react';
import { DeliveryOption, ReceivedDeliveryOption, Shipment } from '../models/DeliveryOptionDetailsModel';
import { getCarrierIcons } from './CarrierIcons';

interface CarrierSectionProps {
  shipment: Shipment;  
}

const CarrierSection: React.FC<CarrierSectionProps> = ({
  shipment,
}) => {
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setOpenStates(prev => ({ ...prev, [id]: !prev[id] }));

  const getCarrierStatus = (opt: any) => {
    const detail = opt?.ShippingDetails;    
    const method = (detail?.DeliveryMethod || "").toLowerCase();
    const carrierId = detail?.CarrierServiceId;
    const validCarrierCode = carrierId && carrierId.length > 0;
    const networkId = detail?.CarrierNetworkId;
    const pudoNetworkNotSet = networkId === "*" || networkId === "";
    return (method === "pudo" && pudoNetworkNotSet) || !validCarrierCode ? "error" : "good";
  };

  return (
    <div className="space-y-2">
      {shipment.DeliveryOptions?.map((opt: DeliveryOption, oIdx: number) => {
        const detail = opt?.ShippingDetails;
        const optionKey = `opt-${oIdx}`;
        const centiroToggleKey = `${optionKey}-centiro-expanded`;
        const { methodIcon, typeIcon} = getCarrierIcons({ DeliveryMethod: detail.DeliveryMethod, DeliveryType: detail.DeliveryType });
        const statusClass = getCarrierStatus(opt);
        const pickupRaw = detail?.EstimatedShippingDate || "TBA";
        const pickupParts = pickupRaw.split(/[T ]/);
        const pickupTime = pickupParts[1]?.substring(0, 5) || "00:00";
        const isErrorTime = pickupTime === "00:00";
        const timeToolTip = isErrorTime ? "Shipping Cutoff Time not returned from Centiro. Could be wrong zipcode, or misconfiguration" : `Shipping Cutoff time: ${pickupTime}`;

        return (
          <div key={optionKey} className="bg-white border-2 border-teal-300 rounded-lg overflow-hidden shadow-sm">
            <div 
                style={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '10px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
                }}
                className="bg-teal-100/30 hover:bg-teal-100" 
                onClick={() => toggle(optionKey)}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                <div>
                  <img src={methodIcon} alt="method" title={detail.DeliveryMethod} className="deliveryIcon shrink-0 h-5 w-5" />
                  <img src={typeIcon} alt="type" title={detail.DeliveryType} className="deliveryIcon shrink-0 h-5 w-5 -ml-1" />
                </div>
                <span className={`text-[11px] font-black uppercase shrink-0 ${statusClass === 'error' ? 'text-red-600' : 'text-black'}`}>
                    {detail?.Carrier || "Standard"}
                </span>
                <span className="text-[10px] font-mono font-black text-teal-600 opacity-70">
                    [{opt.EstimatedDeliveryDate}]
                </span>
              </div>
              <div className="shrink-0 ml-2">
                {openStates[optionKey] ? <ChevronUp size={14} className="text-teal-900" /> : <ChevronDown size={14} className="text-teal-900" />}
              </div>
            </div>
            {openStates[optionKey] && (
              <div className="px-3 pb-3 pt-2 bg-slate-50 border-t-2 border-teal-200 text-[11px]">
                <table className="w-full table-fixed border-collapse" width="100%">
                    <tbody>
                        <tr>
                            <td style={{ width: '50%' }} className="align-top pr-4 border-r border-slate-200">
                                <table className="w-full border-separate border-spacing-y-1 text-black">
                                    <tbody>
                                        <tr>
                                            <td className="w-[85px] text-slate-500 font-bold uppercase tracking-tight pr-2">Ship Date</td>
                                            <td className="w-[10px] text-slate-400 font-bold">:</td>
                                            <td className="font-black text-black pl-2">{pickupParts[0]} <span className={isErrorTime ? "error" : "good"} title={timeToolTip}>{pickupTime}</span></td>
                                        </tr>
                                        <tr>
                                            <td className="w-[85px] text-slate-500 font-bold uppercase tracking-tight pr-2">Delivery Date</td>
                                            <td className="w-[10px] text-slate-400 font-bold">:</td>
                                            <td className="font-black text-black pl-2">{opt.EstimatedDeliveryDate}</td>                                            
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                            <td style={{ width: '50%' }} className="align-top pl-4 overflow-hidden">
                                <table className="w-full border-separate border-spacing-y-1 text-black">
                                    <tbody>
                                        <tr>
                                            <td className="w-[70px] text-slate-500 font-bold uppercase tracking-tight pr-2">Method</td>
                                            <td className="w-[10px] text-slate-400 font-bold">:</td>
                                            <td className="font-black text-black pl-2 lowercase truncate"><img src={methodIcon} alt="method" title={detail.DeliveryMethod} className="deliveryIcon shrink-0 h-5 w-5" />{detail?.DeliveryMethod || "home"}</td>
                                        </tr>
                                        <tr>
                                            <td className="w-[70px] text-slate-500 font-bold uppercase tracking-tight pr-2">Type</td>
                                            <td className="w-[10px] text-slate-400 font-bold">:</td>
                                            <td className="font-black text-black pl-2 lowercase truncate"><img src={typeIcon} alt="type" title={detail.DeliveryType} className="deliveryIcon shrink-0 h-5 w-5" />{detail?.DeliveryType || "standard"}</td>
                                        </tr>                                            
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* COLLAPSIBLE RECEIVED OPTIONS (CENTIRO) */}
                {opt.CentiroOptions.length > 0 && (
                  <div style={{ marginTop: '12px', borderTop: '1px dashed #cbd5e1', paddingTop: '8px' }}>
                    <div 
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: '8px' }}
                      onClick={() => toggle(centiroToggleKey)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <List size={12} />
                        <span style={{ fontSize: '11px'}}>Available Options Centiro ({opt.CentiroOptions.length})</span>
                      </div>
                      {openStates[centiroToggleKey] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </div>
                    
                    {openStates[centiroToggleKey] && (
                      <div style={{ display: 'flex', flexDirection: 'row', overflowX: 'auto', gap: '8px', paddingBottom: '8px' }} className="custom-scrollbar">
                        {opt.CentiroOptions.map((rcvOpt: ReceivedDeliveryOption, rIdx: number) => {
                          const isSelected = opt.SelectedCentiroOption.ServiceCode === rcvOpt.ServiceCode;
                          
                          return (
                            <div key={rIdx} style={{ 
                              backgroundColor: isSelected ? '#f0f7ff' : 'white', 
                              border: isSelected ? '2px solid #b3d7ff' : '1px solid #e2e8f0', 
                              borderRadius: '4px', 
                              padding: '6px', 
                              minWidth: '180px', 
                              flexShrink: 0 
                            }}>
                              <div style={{ 
                                fontSize: '9px', 
                                fontWeight: 900, 
                                color: isSelected ? '#0056b3' : '#0f766e', 
                                borderBottom: isSelected ? '1px solid #e1efff' : '1px solid #f1f5f9', 
                                marginBottom: '4px', 
                                paddingBottom: '2px',
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}>
                                <span>{rcvOpt.CarrierName}</span>
                                {isSelected && <Check size={10} strokeWidth={3} />}
                              </div>
                              <table style={{ width: '100%', fontSize: '8px' }}>
                                <tbody>
                                  {Object.entries(rcvOpt).map(([key, val]) => (
                                    <tr key={key}>
                                      <td style={{ fontWeight: 'bold', color: '#64748b', whiteSpace: 'nowrap', paddingRight: '4px' }}>{key}:</td>
                                      <td style={{ color: 'black', wordBreak: 'break-all' }}>{String(val)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CarrierSection;